/**
 * <pdp-section-nav> — scrollspy for the below-the-fold section nav.
 *
 * This element does NOT handle navigation. The links are real anchors and CSS scroll-behavior does
 * the scrolling, so both work with this script absent (ruling R4). All this adds is moving
 * aria-current to the section currently in view.
 *
 * Classic script, not a module: this theme's shared constants are `const`s in classic scripts and
 * are never window properties.
 *
 * HOW THE WINNER IS CHOSEN (one statement of it, not three):
 * `update()` resolves the winner from geometry alone — the LAST section whose top edge has passed
 * a ceiling just under the pinned nav — plus one clamp for the bottom of the document. The
 * IntersectionObserver and the scroll listener are both only TRIGGERS for that recompute; neither
 * of them decides anything. That is deliberate, and the rejected alternatives are recorded on
 * `update()` itself so a future reader does not re-try them.
 */
class PdpSectionNav extends HTMLElement {
  connectedCallback() {
    // The install/specs slot renders TWO real anchors (ruling G) so href + CSS keep working even
    // if this script never loads — both carry .pdp-v2__nav-link in the server-rendered HTML so
    // either one is fully styled/functional on its own, whether or not this script ever runs.
    // Only one of the pair is ever this breakpoint's actual nav destination (ruling R2: no install
    // link on mobile; its mirror: no separate specs link on desktop, since R5 merges it under
    // "Features & Specs") — CSS hides the other one via display:none on its modifier class.
    //
    // Track only the one CSS is actually showing by filtering on rendered visibility
    // (offsetParent === null for a display:none element), NOT by mutating the shared
    // .pdp-v2__nav-link class. An earlier version stripped that class from the dormant twin here;
    // since it is also the sole styling hook, that permanently detached the twin's font, padding,
    // position:relative (so its ::after underline could never paint) and aria-current styling the
    // moment the breakpoint crossed 990 — a resize or tablet rotation would reveal a raw, unstyled
    // blue link still being tracked by (or newly abandoned by) the scrollspy. Filtering here
    // leaves every anchor's classes and styling untouched at all times.
    //
    // The two node lists themselves are read ONCE: the nav and its sections are server-rendered
    // and nothing on this page adds or removes either (product-info.js re-renders only the buy
    // box's <variant-selects> subtree, which lives outside this element entirely — asserted by the
    // "SEQUENCE: switch variant, then use the nav" test). Which of the twins is SHOWING does
    // change, so that is recomputed per mark() rather than frozen here.
    this.allLinks = Array.from(this.querySelectorAll('.pdp-v2__nav-link'));
    this.sections = Array.from(this.querySelectorAll('.pdp-v2__section'));
    if (!this.allLinks.length || !this.sections.length) return;

    // Reads the height AND builds the observer around it -- one path at connect and at every
    // resize, so the two can never disagree (fix round 1, review finding 3).
    this.readNavHeight();

    // One rAF frame shared by scrolling and resizing. `needsHeight` is a FLAG rather than a second
    // guard because the two events collide: a resize arriving while a scroll frame is already
    // pending used to be dropped entirely by the `if (this.frame) return;` early exit, and the
    // frame that did run recomputed the winner against the OLD nav height (fix round 1, review
    // finding 4). Setting the flag makes the pending frame do the resize's work instead of
    // swallowing it, and consuming it inside the callback keeps it to one height read per frame.
    this.needsHeight = false;
    const schedule = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (this.needsHeight) {
          this.needsHeight = false;
          this.readNavHeight();
        }
        this.update();
      });
    };

    this.onScroll = schedule;
    window.addEventListener('scroll', this.onScroll, { passive: true });

    // Crossing 990 without scrolling changes BOTH inputs to the winner: which twin anchor is
    // visible (ruling G) and the nav's own rendered height (57 -> 80, ruling BE). Without this,
    // rotating a tablet or dragging a window across the breakpoint leaves aria-current sitting on
    // the anchor CSS has just hidden, and leaves the ceiling 23px wrong until the next scroll.
    this.onResize = () => {
      this.needsHeight = true;
      schedule();
    };
    window.addEventListener('resize', this.onResize, { passive: true });

    this.update();
  }

  /**
   * The nav's rendered height, which is also every section's scroll-margin-top (both come from
   * --pdp-nav-h, ruling R7/H). Breakpoint-dependent since ruling BE, so never cache it past a
   * resize. `?? 0` rather than a fallback constant: a missing nav means a ceiling of 1px, which
   * degrades to "the section whose top has passed the viewport top" — wrong by 57-80px, but never
   * wrong by a made-up number that would then get copied somewhere else.
   *
   * Rebuilds the observer when (and only when) the height actually moved. The observer's
   * rootMargin is the one other place the height appears, and leaving it baked in at connect time
   * contradicted this method's whole reason for existing (fix round 1, review finding 3). Guarding
   * on a real change keeps a resize storm from tearing down and re-observing four sections per
   * frame for a height that did not move.
   */
  readNavHeight() {
    const h = this.querySelector('.pdp-v2__section-nav')?.offsetHeight ?? 0;
    if (h === this.navHeight) return;
    this.navHeight = h;
    this.observeSections();
  }

  /**
   * rootMargin pulls the observation band to just under the sticky nav, so a section counts as
   * "in view" when it is where the shopper is actually reading, not when its first pixel appears.
   *
   * The -40% bottom shrink (not the more common ~-55%) keeps the band reachable for a short
   * trailing section like `reviews`: on a details container this tall relative to this store's
   * real footer, a steeper shrink can put the band's floor past the page's own maximum scroll
   * position, so the last section could never trigger a recompute at all. It is a TRIGGER
   * threshold only — the -40% floor does not select a winner and never did; update() does.
   */
  observeSections() {
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(() => this.update(), {
      rootMargin: `-${this.navHeight}px 0px -40% 0px`,
      threshold: 0,
    });
    this.sections.forEach((s) => this.observer.observe(s));
  }

  /**
   * The winner is the LAST section whose top edge has passed the band's ceiling -- i.e. the one the
   * shopper has most recently scrolled into. Pure geometry, so it cannot depend on the order the
   * observer happens to deliver entries in. Above all of them, the first section wins.
   *
   * The `+ 1` is a tolerance, not a magic number: a section's top and the nav's bottom are meant to
   * coincide exactly after an anchor jump (scroll-margin-top === --pdp-nav-h), and both sides of
   * that comparison are fractional -- the nav is 56.984/79.984 rendered, sections carry 40.7px of
   * padding, and browsers round scroll offsets. 1px absorbs that without ever reaching the next
   * section, whose nearest neighbour here is 40px away (`install` at its shortest). Do not raise it
   * to "be safe": a larger tolerance marks the NEXT section slightly before its top arrives.
   *
   * Two winner rules were tried before this one and both are wrong, which is worth recording:
   *
   *   "last entry in the callback batch"  (what originally shipped) -- arbitrary. Two sections are
   *     in the band constantly: features and specs sit side by side at 990+ (ruling R5), and the
   *     short install/reviews pair is adjacent everywhere. Whichever landed last in `entries` took
   *     the marking, so "Installation Notes & Certifications" could never hold it -- reviews always
   *     followed it in the same batch. On desktop it was worse than arbitrary: `specs` has no
   *     visible link at 990+, so its turn cleared the whole nav (see mark()).
   *   "first intersecting in document order" -- too sticky. The features column is ~1000px tall
   *     at 990+, so it is still intersecting when install reaches the band and the marking lags a
   *     whole section behind (measured: scrolling to install still read "Features & Specs").
   *
   * A scroll listener is needed alongside the observer, not instead of it: with a -40% bottom
   * margin a section STARTS intersecting when its top reaches the band's floor, and no further
   * entry fires while its top travels up to the band's ceiling -- which is the crossing that
   * actually changes the winner.
   *
   * One fact that makes the walk-back in mark() load-bearing rather than decorative: at 990+
   * `features` and `specs` are two tracks of the SAME grid row (ruling R5), so their tops are
   * IDENTICAL. `specs` is later in document order, so it always wins the tie -- which means the
   * whole desktop path from the top of the container down to `install` resolves through
   * mark()'s "nearest preceding section with a visible link" walk, not directly.
   */
  update() {
    const ceiling = this.navHeight + 1;
    let winner = this.sections[0];
    for (const s of this.sections) {
      if (s.getBoundingClientRect().top <= ceiling) winner = s;
    }
    // Bottom-of-document clamp. The ceiling rule assumes every section can be scrolled up to it,
    // and the LAST one cannot: the page stops scrolling while its top is still well below the nav
    // whenever it is shorter than the viewport. That is not hypothetical here -- the reviews
    // section is a mount for a vendor app block, and when that block renders nothing the section
    // collapses to its visually-hidden <h2> (~91px desktop / ~41px mobile), leaving its top around
    // y=585 at maximum scroll. "Reviews" could then never bold, and clicking it would visibly
    // settle back onto "Installation Notes & Certifications" a frame later -- the marking
    // contradicting the shopper's own click. At the end of the document the last section is what
    // the shopper is looking at, by definition, so say so.
    const atEnd = window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;
    if (atEnd) winner = this.sections[this.sections.length - 1];
    this.mark(winner.dataset.key);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  mark(key) {
    if (!key) return;
    const visible = this.allLinks.filter((a) => a.offsetParent !== null);
    if (!visible.length) return;

    // Not every section has a link at every breakpoint, by design: `specs` has none at 990+ (ruling
    // R5 merges it into "Features & Specs") and `install` has none below 990 (ruling R2). The old
    // code's comment claimed it left the marking alone in that case; it did not -- it fell through
    // the loop matching nothing and cleared aria-current from EVERY link. That is why, on desktop,
    // scrolling through features or specs left the whole nav unbold: both are in the band together,
    // and `specs` resolved to no link at all. Reported from the live page; every scrollspy test in
    // the suite CLICKED, which lands on the one position the old code got right.
    //
    // Resolve a keyless section to the nearest PRECEDING section that does have a visible link.
    // Derived from DOM order rather than a hardcoded map, and it gives the right answer at both
    // breakpoints: specs -> features at 990+, install -> specs below it.
    let index = this.sections.findIndex((s) => s.dataset.key === key);
    let target = null;
    while (index >= 0 && !target) {
      const k = this.sections[index].dataset.key;
      target = visible.find((a) => a.dataset.key === k) || null;
      index -= 1;
    }
    // Nothing resolved (a section above every linked one): leave the previous marking standing
    // rather than blanking the nav -- which is what the original comment intended all along.
    if (!target) return;

    // Clear across ALL anchors, not just the visible ones: a stale aria-current left on the dormant
    // twin would be read out by assistive tech the moment a resize revealed it.
    this.allLinks.forEach((a) => {
      if (a === target) a.setAttribute('aria-current', 'true');
      else a.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('pdp-section-nav')) customElements.define('pdp-section-nav', PdpSectionNav);
